/**
 * Admin organizations list — search + dense DataTable. Search is applied on submit (and via a
 * Search button). Each row links to the org detail screen; a "New comped org" button opens the
 * provisioning form.
 */
import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import type { OrgSummary } from '@beam/api-types';
import { adminListOrgs, AdminApiError } from '../../../src/api/adminClient';
import { Screen, Text, Card, Badge, Field, Button, DataTable, type Column } from '../../../src/components/ui';
import { licenseBadge } from '../../../src/components/ui';
import { AdminHeader } from '../../../src/components/admin/AdminHeader';
import { theme } from '../../../src/theme';

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

export default function AdminOrgsScreen() {
  const router = useRouter();
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'orgs', search],
    queryFn: () => adminListOrgs({ search: search || undefined, limit: 100 }),
  });

  const columns: Column<OrgSummary>[] = [
    { key: 'legalName', header: 'Organization', flex: 2, render: (r) => <Text variant="bodyStrong" numberOfLines={1}>{r.legalName}</Text> },
    { key: 'ownerEmail', header: 'Owner', flex: 2, render: (r) => <Text variant="small" color="secondary" numberOfLines={1}>{r.ownerEmail ?? '—'}</Text> },
    {
      key: 'status',
      header: 'License',
      flex: 1.2,
      render: (r) => {
        if (!r.status) return <Text variant="small" color="secondary">—</Text>;
        const spec = licenseBadge(r.status);
        return <Badge status={spec.key} label={spec.label} />;
      },
    },
    { key: 'plan', header: 'Plan', flex: 1, render: (r) => <Text variant="small" numberOfLines={1}>{r.plan ?? '—'}</Text> },
    { key: 'seats', header: 'Seats', flex: 0.7, align: 'right', render: (r) => <Text variant="data">{r.seats ?? '—'}</Text> },
    { key: 'isComped', header: 'Comped', flex: 0.9, render: (r) => (r.isComped ? <Badge status="sent" label="Comped" /> : <Text variant="small" color="secondary">—</Text>) },
    { key: 'createdAt', header: 'Created', flex: 1, align: 'right', render: (r) => <Text variant="small" color="secondary">{formatDate(r.createdAt)}</Text> },
    {
      key: 'open',
      header: '',
      flex: 0.8,
      align: 'right',
      render: (r) => (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Open ${r.legalName}`}
          onPress={() => router.push(`/admin/orgs/${r.organizationId}`)}
          style={({ pressed }) => [styles.openLink, pressed && styles.rowLinkPressed]}
        >
          <Text variant="small" color="link">
            Open →
          </Text>
        </Pressable>
      ),
    },
  ];

  function applySearch() {
    setSearch(input.trim());
  }

  return (
    <Screen scroll maxWidth={null}>
      <AdminHeader active="orgs" />

      <View style={styles.toolbar}>
        <View style={styles.searchField}>
          <Field
            label="Search organizations"
            value={input}
            onChangeText={setInput}
            autoCapitalize="none"
            autoCorrect={false}
            placeholder="Legal name or owner email"
            returnKeyType="search"
            onSubmitEditing={applySearch}
          />
        </View>
        <Button title="Search" size="md" variant="secondary" fullWidth={false} onPress={applySearch} style={styles.toolbarBtn} />
        <Button title="New comped org" size="md" fullWidth={false} onPress={() => router.push('/admin/orgs/new')} style={styles.toolbarBtn} />
      </View>

      {isLoading ? (
        <Card>
          <Text color="secondary">Loading organizations…</Text>
        </Card>
      ) : error ? (
        <Card>
          <Text color="danger">{error instanceof AdminApiError || error instanceof Error ? error.message : 'Failed to load organizations.'}</Text>
        </Card>
      ) : !data || data.orgs.length === 0 ? (
        <Card>
          <Text color="secondary">No organizations found.</Text>
        </Card>
      ) : (
        <Card style={styles.tableCard}>
          <Text variant="small" color="secondary">
            {data.total} organization{data.total === 1 ? '' : 's'}
          </Text>
          <DataTable
            columns={columns}
            rows={data.orgs}
            keyExtractor={(r) => r.organizationId}
          />
        </Card>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  toolbar: { flexDirection: 'row', alignItems: 'flex-end', gap: theme.spacing.md, flexWrap: 'wrap' },
  searchField: { flex: 1, minWidth: 240 },
  toolbarBtn: { alignSelf: 'flex-end' },
  tableCard: { gap: theme.spacing.sm },
  openLink: { paddingVertical: theme.spacing.xs },
  rowLinkPressed: { opacity: 0.6 },
});
